import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Download, FileText, IndianRupee } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const GSTTCSReport = () => {
  const [granularity, setGranularity] = useState('monthly');
  const today = new Date();
  const yyyy = today.getFullYear();
  const startOfYear = `${yyyy}-04-01`; // Indian FY default
  const endToday = today.toISOString().slice(0, 10);
  const [start, setStart] = useState(startOfYear);
  const [end, setEnd] = useState(endToday);
  const [data, setData] = useState({ rows: [], byPan: [], byBucket: [], totals: { rows: 0, base: 0, gst: 0, tcs: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('rows'); // rows | byPan | byBucket

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ granularity, start, end });
      const res = await axios.get(`${BACKEND_URL}/api/accounting/reports/gst-tcs?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load GST/TCS report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularity, start, end]);

  // CSV download — flat rows view always, with the period+granularity stamp
  // in the filename so re-downloads don't collide.
  const downloadCSV = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = [
      'Bucket', 'Invoice Date', 'Pax Name', 'Trip ID', 'Invoice #',
      'PAN', 'Passport', 'GSTIN', 'Address', 'Customer Type', 'B2B/B2C',
      'Destination', 'Place of Supply', 'Supply Type',
      'Base', 'CGST', 'SGST', 'IGST', 'GST', 'TCS', 'Total',
    ];
    const lines = [headers.join(',')];
    for (const r of data.rows) {
      const row = [
        esc(r.bucket),
        esc(r.invoiceDate),
        esc(r.paxName),
        esc(r.displayTripId || r.tripId),
        esc(r.invoiceNumber),
        esc(r.panNumber),
        esc(r.passportNumber),
        esc(r.gstin),
        esc(r.address),
        esc(r.customerType),
        esc(r.businessType),
        esc(r.destination),
        esc(r.placeOfSupply),
        esc(r.supplyType),
        r.base,
        r.cgst,
        r.sgst,
        r.igst,
        r.gst,
        r.tcs,
        r.total,
      ];
      lines.push(row.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gst-tcs-${granularity}-${start}_to_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.rows.length} rows`);
  };

  // CSV for the per-PAN aggregation view.
  const downloadPanCSV = () => {
    const headers = ['PAN', 'Rows', 'Base', 'GST', 'TCS', 'Total'];
    const lines = [headers.join(',')];
    for (const r of data.byPan) {
      lines.push([r.pan || 'NO PAN', r.rows, r.base, r.gst, r.tcs, r.total].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gst-tcs-by-pan-${start}_to_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data.totals || {};
  const noPanRows = useMemo(
    () => data.rows.filter((r) => !r.panNumber).length,
    [data.rows],
  );

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="gst-tcs-report-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-emerald-600" />
            GST &amp; TCS Report
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Per-invoice + per-PAN tax view across the chosen period.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={downloadCSV}
            data-testid="download-csv-btn"
            className="gap-2"
          >
            <Download className="h-4 w-4" /> Export Rows
          </Button>
          <Button
            variant="outline"
            onClick={downloadPanCSV}
            data-testid="download-pan-csv-btn"
            className="gap-2"
          >
            <Download className="h-4 w-4" /> Export by PAN
          </Button>
        </div>
      </header>

      {/* Filters */}
      <Card className="p-4 flex flex-wrap gap-3 items-end" data-testid="report-filters">
        <div>
          <Label className="text-xs">Granularity</Label>
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="w-40 mt-1" data-testid="granularity-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Start</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40 mt-1" data-testid="start-date-input" />
        </div>
        <div>
          <Label className="text-xs">End</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40 mt-1" data-testid="end-date-input" />
        </div>
        <div className="ml-auto inline-flex bg-slate-100 rounded-lg p-0.5">
          {[
            { k: 'rows', label: 'Rows' },
            { k: 'byPan', label: 'By PAN' },
            { k: 'byBucket', label: 'By Period' },
          ].map((v) => (
            <button
              type="button"
              key={v.k}
              onClick={() => setView(v.k)}
              data-testid={`view-${v.k}`}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                view === v.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Totals strip */}
      <Card className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-100" data-testid="totals-strip">
        <Cell label="Rows" value={totals.rows ?? 0} />
        <Cell label="Base" value={fmtINR(totals.base)} />
        <Cell label="GST" value={fmtINR(totals.gst)} tone="emerald" />
        <Cell label="TCS" value={fmtINR(totals.tcs)} tone="emerald" />
        <Cell label="Grand Total" value={fmtINR(totals.total)} tone="dark" />
      </Card>

      {noPanRows > 0 && (
        <div
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg"
          data-testid="missing-pan-warning"
        >
          {noPanRows} row{noPanRows === 1 ? '' : 's'} {noPanRows === 1 ? 'has' : 'have'} no PAN on file — add the PAN to the
          traveller's profile or to the invoice split to get accurate per-PAN reporting.
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm" data-testid="report-loading">Loading…</div>
      ) : view === 'rows' ? (
        <RowsTable rows={data.rows} />
      ) : view === 'byPan' ? (
        <ByPanTable rows={data.byPan} />
      ) : (
        <ByBucketTable rows={data.byBucket} />
      )}
    </div>
  );
};

function Cell({ label, value, tone }) {
  const colorClass = tone === 'emerald' ? 'text-emerald-700'
    : tone === 'dark' ? 'text-slate-900 font-bold'
    : 'text-slate-700';
  return (
    <div className="p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-mono mt-1 ${colorClass}`}>{value}</div>
    </div>
  );
}

function RowsTable({ rows }) {
  if (rows.length === 0) {
    return <EmptyState />;
  }
  return (
    <Card className="overflow-x-auto" data-testid="rows-table">
      <table className="w-full">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr>
            <Th>Month</Th>
            <Th>Inv Date</Th>
            <Th>Pax Name</Th>
            <Th>Trip ID</Th>
            <Th>Invoice #</Th>
            <Th>PAN</Th>
            <Th>Passport</Th>
            <Th>GSTIN</Th>
            <Th>Address</Th>
            <Th>Customer</Th>
            <Th>B2B/B2C</Th>
            <Th>Destination</Th>
            <Th>Place of Supply</Th>
            <Th>Supply</Th>
            <Th align="right">Base</Th>
            <Th align="right">CGST</Th>
            <Th align="right">SGST</Th>
            <Th align="right">IGST</Th>
            <Th align="right">GST</Th>
            <Th align="right">TCS</Th>
            <Th align="right">Total</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r, i) => (
            <tr key={i} data-testid={`report-row-${i}`} className="text-xs hover:bg-slate-50">
              <Td>{r.bucket}</Td>
              <Td className="font-mono whitespace-nowrap">{r.invoiceDate || '—'}</Td>
              <Td>{r.paxName || '—'}</Td>
              <Td className="font-mono">{r.displayTripId || r.tripId || '—'}</Td>
              <Td className="font-mono">{r.invoiceNumber}</Td>
              <Td className={`font-mono ${!r.panNumber ? 'text-rose-600' : ''}`}>{r.panNumber || 'NO PAN'}</Td>
              <Td className="font-mono">{r.passportNumber || '—'}</Td>
              <Td className="font-mono">{r.gstin || '—'}</Td>
              <Td className="max-w-[180px] truncate" title={r.address || ''}>{r.address || '—'}</Td>
              <Td>{r.customerType || '—'}</Td>
              <Td>{r.businessType || '—'}</Td>
              <Td>{r.destination || '—'}</Td>
              <Td>{r.placeOfSupply || '—'}</Td>
              <Td className="capitalize">{r.supplyType || '—'}</Td>
              <Td align="right" className="font-mono">{fmtNum(r.base)}</Td>
              <Td align="right" className="font-mono">{fmtNum(r.cgst)}</Td>
              <Td align="right" className="font-mono">{fmtNum(r.sgst)}</Td>
              <Td align="right" className="font-mono">{fmtNum(r.igst)}</Td>
              <Td align="right" className="font-mono text-emerald-700">{fmtNum(r.gst)}</Td>
              <Td align="right" className="font-mono text-emerald-700">{fmtNum(r.tcs)}</Td>
              <Td align="right" className="font-mono font-semibold">{fmtNum(r.total)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function ByPanTable({ rows }) {
  if (rows.length === 0) return <EmptyState />;
  return (
    <Card className="overflow-x-auto" data-testid="by-pan-table">
      <table className="w-full">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr>
            <Th>PAN</Th>
            <Th align="right">Invoices</Th>
            <Th align="right">Base</Th>
            <Th align="right">GST</Th>
            <Th align="right">TCS</Th>
            <Th align="right">Total</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r, i) => (
            <tr key={r.pan || `nopan-${i}`} data-testid={`bypan-row-${i}`} className="text-xs hover:bg-slate-50">
              <Td className={`font-mono ${!r.pan ? 'text-rose-600' : ''}`}>{r.pan || 'NO PAN'}</Td>
              <Td align="right">{r.rows}</Td>
              <Td align="right" className="font-mono">{fmtNum(r.base)}</Td>
              <Td align="right" className="font-mono text-emerald-700">{fmtNum(r.gst)}</Td>
              <Td align="right" className="font-mono text-emerald-700">{fmtNum(r.tcs)}</Td>
              <Td align="right" className="font-mono font-semibold">{fmtNum(r.total)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function ByBucketTable({ rows }) {
  if (rows.length === 0) return <EmptyState />;
  return (
    <Card className="overflow-x-auto" data-testid="by-bucket-table">
      <table className="w-full">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr>
            <Th>Period</Th>
            <Th align="right">Invoices</Th>
            <Th align="right">Base</Th>
            <Th align="right">GST</Th>
            <Th align="right">TCS</Th>
            <Th align="right">Total</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r) => (
            <tr key={r.bucket} data-testid={`bybucket-row-${r.bucket}`} className="text-xs hover:bg-slate-50">
              <Td className="font-mono font-semibold">{r.bucket}</Td>
              <Td align="right">{r.rows}</Td>
              <Td align="right" className="font-mono">{fmtNum(r.base)}</Td>
              <Td align="right" className="font-mono text-emerald-700">{fmtNum(r.gst)}</Td>
              <Td align="right" className="font-mono text-emerald-700">{fmtNum(r.tcs)}</Td>
              <Td align="right" className="font-mono font-semibold">{fmtNum(r.total)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const Th = ({ children, align }) => (
  <th className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'}`}>{children}</th>
);
const Td = ({ children, align, className = '' }) => (
  <td className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}>{children}</td>
);

const EmptyState = () => (
  <Card className="text-center py-12 text-slate-400 text-sm" data-testid="empty-state">
    <IndianRupee className="h-8 w-8 mx-auto text-slate-300 mb-2" />
    No invoices in this date range.
  </Card>
);

export default GSTTCSReport;
